import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { trademarkApi } from "../api/trademarkApi";
import { useAuth } from "./AuthProvider";

/**
 * Holds the one search the app is currently showing.
 *
 * Why a provider rather than passing props: the search is started on one
 * route and read on another, so the result has to outlive the page that
 * created it. We also mirror it into sessionStorage, so reloading or deep
 * linking /results doesn't drop the user back to an empty screen.
 *
 * That storage is scoped to whoever is signed in (see `ownerId` below).
 * sessionStorage isn't cleared on logout, so without this, signing out and
 * a different person signing in on the same tab would load the previous
 * person's last search straight into the results table — including an
 * admin's results appearing for a regular user.
 */

const SearchContext = createContext(null);
const STORAGE_KEY = "gmft:last-search";

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(data) {
  try {
    if (data) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage full or blocked — the in-memory copy still works.
  }
}

export function SearchProvider({ children }) {
  const { user } = useAuth();
  const currentUserId = user?.id || null;

  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState(null);
  const [retryNotice, setRetryNotice] = useState(null);
  const [lastQuery, setLastQuery] = useState(null);

  // `undefined` means "we haven't looked at who's signed in yet" — lets the
  // first effect run tell a genuine account switch apart from the initial
  // mount, where we instead try to restore that same user's last search.
  const knownUserId = useRef(undefined);

  useEffect(() => {
    if (knownUserId.current === currentUserId) return;

    const isFirstCheck = knownUserId.current === undefined;
    knownUserId.current = currentUserId;

    const stored = isFirstCheck ? readStored() : null;
    const belongsToCurrentUser =
      stored && currentUserId && stored.ownerId === currentUserId;

    if (belongsToCurrentUser) {
      setData(stored);
      setLastQuery(stored.query || null);
      setStatus("ready");
      return;
    }

    // Different account (or no account) — never carry another user's
    // results across the switch, in memory or in storage.
    setData(null);
    setLastQuery(null);
    setError(null);
    setStatus("idle");
    writeStored(null);
  }, [currentUserId]);

  const runSearch = useCallback(
    async (payload) => {
      setStatus("loading");
      setError(null);
      setRetryNotice(null);
      setLastQuery(payload);

      try {
        const result = await trademarkApi.search(payload, ({ attempt, of }) =>
          setRetryNotice(
            `The register didn't answer. Retrying (${attempt} of ${of})…`,
          ),
        );
        setData(result);
        writeStored({ ...result, ownerId: currentUserId });
        setStatus("ready");
        return result;
      } catch (err) {
        setError(err.message);
        setData(null);
        writeStored(null);
        setStatus("error");
        throw err;
      } finally {
        setRetryNotice(null);
      }
    },
    [currentUserId],
  );

  const clearSearch = useCallback(() => {
    setData(null);
    setError(null);
    setStatus("idle");
    writeStored(null);
  }, []);

  /** Load a saved search from history into the results view. */
  const loadSnapshot = useCallback(
    (snapshot) => {
      const shaped = {
        source: snapshot.source,
        query: snapshot.query,
        resultsCount: snapshot.resultsCount,
        results: snapshot.results || [],
        aiInsight: snapshot.aiInsight,
        durationMs: snapshot.durationMs,
        notices: [],
        historyId: snapshot._id,
      };
      setData(shaped);
      setLastQuery(snapshot.query);
      setStatus("ready");
      writeStored({ ...shaped, ownerId: currentUserId });
      return shaped;
    },
    [currentUserId],
  );

  const value = useMemo(
    () => ({
      data,
      status,
      error,
      retryNotice,
      lastQuery,
      runSearch,
      clearSearch,
      loadSnapshot,
    }),
    [
      data,
      status,
      error,
      retryNotice,
      lastQuery,
      runSearch,
      clearSearch,
      loadSnapshot,
    ],
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context)
    throw new Error("useSearch must be used inside a SearchProvider.");
  return context;
}
